import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  /** Bumping this remounts the chart on user request. */
  resetKey?: string | number
  children: ReactNode
}

interface State {
  error: Error | null
}

/**
 * Local boundary that contains chart/render crashes to the chart slot itself.
 * The rest of the page (trade panel, header, etc.) stays interactive instead
 * of the top-level ErrorBoundary nuking the whole shell.
 *
 * For known-sporadic lightweight-charts v5 internal throws (hit-test race,
 * "Value is null"), auto-recover immediately rather than showing a retry tile
 * — the next data tick will succeed.
 */
function isRecoverableChartError(error: Error): boolean {
  if (!error?.message) return false
  if (!error.message.includes('Value is null')) return false
  const stack = error.stack ?? ''
  return stack.includes('lightweight-charts')
}

export class ChartErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (isRecoverableChartError(error)) {
      console.warn('[ChartErrorBoundary] swallowed lightweight-charts error', error.message)
      // Reset on the next tick so React commits the error state first, then
      // we re-render the children fresh. Without the deferral React would
      // hit the same error on the same render and bail out.
      queueMicrotask(() => this.setState({ error: null }))
      return
    }
    console.error('[ChartErrorBoundary]', error, info)
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null })
    }
  }

  reset = () => this.setState({ error: null })

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <div className="w-full h-full min-h-[200px] flex items-center justify-center bg-zinc-950 border border-zinc-800 rounded">
        <div className="text-center px-4 py-6 max-w-sm">
          <div className="text-sm text-zinc-300 mb-1">Chart couldn't render</div>
          <div className="text-xs text-zinc-500 mb-3 break-all">
            {error.message || 'Unknown error'}
          </div>
          <button
            type="button"
            onClick={this.reset}
            className="text-xs text-emerald-400 hover:text-emerald-300 border border-zinc-700 hover:border-zinc-600 rounded px-3 py-1.5 transition"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }
}

export default ChartErrorBoundary
